import moment from "moment";
import Select from "react-select";
import { useHistory, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import { useEffect, useState } from "react";
import { Option } from "../../store/helpers";
import { objectToParams, paramsToObject } from "../../store/api";
import { RootState } from "../../store/rootReducer";
import { useDispatch, useSelector } from "react-redux";
import { showModal } from "../../reducers/modal/modal.actions";
import { DokumenKerjasamaListRequest } from "../../reducers/dokumenKerjasama/types";

/**
 * Dokumen Kerjasama List Page: Filter
 * This component is used to set filter settings on dokumen
 * kerjasama list page. This can also be used to filter
 * export excel fields.
 */
const DokumenKerjasamaListPageFilter = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const listDepartment = useSelector((state: RootState) => state.department.listDepartment);
  const listJenisDokumen = useSelector((state: RootState) => state.jenisDokumen.listJenisDokumen);
  const isLoading = useSelector((state: RootState) => state.dokumenKerjasama.isLoading);

  const [title, setTitle] = useState<string>('');
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [jenisDokumen, setJenisDokumen] = useState<number>(-1);
  const [department, setDepartment] = useState<string>('0000000000');
  const [departmentOptions, setDepartmentOptions] = useState<Option[]>([]);
  const [jenisDokumenOptions, setJenisDokumenOptions] = useState<Option[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<Option>({ label: 'NONE', value: '0000000000' });
  const [sisa_berlaku, setDurasi] = useState<number>();

  /**
   * Update filter by start date on use state above based on user
   * input.
   * @param date: date class
   */
  const handleChangeStartDate = (date: Date) => {
    if (moment(date).diff(endDate) > 0) {
      dispatch(showModal('Proses mengganti tanggal gagal', 'Tanggal mulai harus lebih kecil dari tanggal selesai'));
      return;
    }
    setStartDate(date);
  };

  /**
   * Update filter by end date on use state above based on user
   * input.
   * @param date: date class
   */
  const handleChangeEndDate = (date: Date) => {
    if (moment(date).diff(startDate) < 0) {
      dispatch(showModal('Proses mengganti tanggal gagal', 'Tanggal selesai harus lebih besar dari tanggal mulai'));
      return;
    }
    setEndDate(date);
  };

  /**
   * Update filter by jenis dokumen based on option selected on
   * use state above.
   */
  const handleChangeJenisDokumen = (e: Option | null) => {
    const value = e?.value;
    if (!value) return;
    if (isNaN(parseInt(value))) return;
    setJenisDokumen(parseInt(value));
  };

  /**
   * Update filter by jenis dokumen based on option selected on
   * use state above.
   */
   const handleChangeDepartment = (e: Option | null) => {
    if (!e) return;
    const value = e.value;
    setDepartment(value);
    setSelectedDepartment(e);
  };

  /**
   * Update filter by document title field based on user input.
   */
  const onChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const onChangeDurasi = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDurasi(parseInt(e.target.value));
  }

  /**
   * Execute filter provided and update displayed data on
   * DokumenKerjasamaListPage component. Also updates jenis
   * dokumen options from API.
   */
  const filter = () => {
    if (isLoading) return;
    const filter: DokumenKerjasamaListRequest = {
      tanggal_awal: moment(startDate).format('YYYY-MM-DD'),
      tanggal_akhir: moment(endDate).format('YYYY-MM-DD'),
      judul_kerjasama: title,
      department: department,
      jenis_dokumen: jenisDokumen,
      sisa_berlaku: sisa_berlaku
    };
    if (filter.jenis_dokumen && filter.jenis_dokumen < 0) delete filter.jenis_dokumen;
    if (!filter.judul_kerjasama) delete filter.judul_kerjasama;
    if (!sisa_berlaku) delete filter.sisa_berlaku
    history.push(`/dokumen-kerjasama${objectToParams(filter)}`);
  };

  /**
   * Listen to API calls for Jenis Dokumen and updates jenis
   * dokumen fields according to the new value.
   */
  useEffect(() => {
    const newOptions: Option[] = [];
    newOptions.push({
      value: '-1',
      label: 'Jenis Dokumen Apapun'
    });
    listJenisDokumen.forEach(item => {
      newOptions.push({
        value: item.DOK_JENIS_ID.toString(),
        label: item.DOK_JENIS_NAMA
      });
    });
    setJenisDokumenOptions(newOptions);
  }, [listJenisDokumen]);

  /**
   * Listen to API calls for Departments and updates department
   * fields according to the new value.
   */
   useEffect(() => {
    const newOptions: Option[] = [];
    listDepartment.forEach(item => {
      newOptions.push({
        value: item.DEPT_ID.toString(),
        label: item.DEPT_NAME
      });
    });
    setDepartmentOptions(newOptions);
  }, [listDepartment]);

  useEffect(() => {
    const filter = paramsToObject(location.search);
    if (!filter || !filter.department) return;
    const selectedOption = listDepartment.find(i => i.DEPT_ID === filter.department);
    if (selectedOption) {
      setSelectedDepartment({
        label: selectedOption.DEPT_NAME,
        value: selectedOption.DEPT_ID
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <div className="box-body table-responsive no-padding">
      <table className="table table-hover">
        <thead>
          <tr>
            <th>Tanggal Mulai</th>
            <th>Tanggal Selesai</th>
            <th>Judul Dokumen</th>
            <th>Jenis Dokumen</th>
            <th>Department</th>
            <th>Sisa Berlaku</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td width='120'>
              <div className="docs-date">
                <DatePicker
                  disabled={isLoading}
                  selected={startDate}
                  showMonthDropdown
                  showYearDropdown
                  onChange={handleChangeStartDate}
                />
              </div>
            </td>
            <td width='120'>
              <div className="docs-date">
                <DatePicker
                  disabled={isLoading}
                  selected={endDate}
                  showMonthDropdown
                  showYearDropdown
                  onChange={handleChangeEndDate}
                />
              </div>
            </td>
            <td width='200'>
              <input
                type="text"
                value={title}
                onChange={onChangeTitle}
                placeholder="Saring berdasarkan judul"
              />
            </td>
            <td width='250'>
              <Select
                onChange={handleChangeJenisDokumen}
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                options={jenisDokumenOptions}
              />
            </td>
            <td width='250'>
              <Select
                onChange={handleChangeDepartment}
                menuPortalTarget={document.body}
                value={selectedDepartment}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                options={departmentOptions}
              />
            </td>
            <td width='100'>
              <input
                type="number"
                value={sisa_berlaku}
                onChange={onChangeDurasi}
                placeholder="Hari"
                style={{width: '100px'}}
                min={1}
                onWheel={(e) => e.currentTarget.blur()}
              />
            </td>
            <td>
              <button
                disabled={isLoading}
                className="btn btn-primary"
                onClick={filter}
              >
                Cari
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default DokumenKerjasamaListPageFilter;